import { useTheme } from "@/src/styles/theme-provider";
import { More } from "iconsax-react-nativejs";
import { ReactElement, useRef, useState } from "react";
import {
  Animated,
  Dimensions,
  Modal,
  Platform,
  Pressable,
  Text,
  View,
} from "react-native";

export type ContextMenuItem = Readonly<{
  label: string;
  onPress: () => void;
  destructive?: boolean;
  textClass?: string;
  rightIcon?: ReactElement;
}>;

export type MoreEllipseProps = Readonly<{
  items: ContextMenuItem[];
  iconColor?: string;
  iconSize?: number;
  menuWidth?: number;
  highlightClass?: string;
  highlightTextClass?: string;
  trigger?: ReactElement;
}>;

const ITEM_HEIGHT = 44;
const MENU_VERTICAL_PADDING = 8;
const MENU_GAP = 4;

export default function DropDown({
  items,
  iconColor,
  iconSize = 24,
  menuWidth = 150,
  highlightClass = "bg-primary-1",
  highlightTextClass = "text-primary",
  trigger,
}: MoreEllipseProps) {
  const [visible, setVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [pressedIndex, setPressedIndex] = useState<number | null>(null);
  const triggerRef = useRef<View>(null);
  const scaleAnim = useRef(new Animated.Value(0.9)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  const { themeVars, varStyle } = useTheme();
  const isWeb = Platform.OS === "web";

  const openMenu = () => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      const screenHeight = Dimensions.get("window").height;
      const estimatedMenuHeight =
        items.length * ITEM_HEIGHT + MENU_VERTICAL_PADDING;

      const spaceBelow = screenHeight - (y + height + MENU_GAP);
      const spaceAbove = y - MENU_GAP;

      const showAbove =
        spaceBelow < estimatedMenuHeight && spaceAbove >= estimatedMenuHeight;

      const menuY = showAbove
        ? y - estimatedMenuHeight - MENU_GAP
        : y + height + MENU_GAP;

      // If custom trigger: anchor to the right of it; otherwise default (left-aligned)
      const screenWidth = Dimensions.get("window").width;
      const spaceOnRight = screenWidth - (x + width);
      const menuX =
        spaceOnRight >= menuWidth
          ? x + width + MENU_GAP
          : x - menuWidth - MENU_GAP;

      setMenuPosition({ x: menuX, y: menuY });
      setVisible(true);
      scaleAnim.setValue(0.9);
      opacityAnim.setValue(0);

      Animated.parallel([
        Animated.spring(scaleAnim, {
          toValue: 1,
          useNativeDriver: true,
          tension: 280,
          friction: 22,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    });
  };

  const closeMenu = (afterClose?: () => void) => {
    Animated.parallel([
      Animated.timing(scaleAnim, {
        toValue: 0.9,
        duration: 110,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 110,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setVisible(false);
      setPressedIndex(null);
      afterClose?.();
    });
  };

  const handleItemPress = (item: ContextMenuItem, index: number) => {
    setPressedIndex(index);
    closeMenu(() => setTimeout(() => item.onPress(), 50));
  };

  const getTextClass = (item: ContextMenuItem, index: number): string => {
    if (item.textClass) return item.textClass;
    if (item.destructive) return "text-crimson";
    if (pressedIndex === index) return highlightTextClass;
    return "text-onyx";
  };

  const getBgClass = (item: ContextMenuItem, index: number): string => {
    if (pressedIndex !== index) return "";
    if (item.destructive) return "bg-crimson-1/10";
    return highlightClass;
  };

  return (
    <>
      <Pressable
        ref={triggerRef}
        onPress={openMenu}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        style={({ pressed }) => ({ opacity: pressed ? 0.4 : 1 })}
        className="p-1.5 hover:bg-ash-3 hover:rounded-full"
      >
        {trigger ?? (
          <More
            size={iconSize}
            color={iconColor ?? (themeVars["--color-slate-default"] as string)}
            className="rotate-90"
          />
        )}
      </Pressable>

      <Modal
        visible={visible}
        transparent
        animationType="none"
        onRequestClose={() => closeMenu()}
        statusBarTranslucent
      >
        <Pressable
          style={varStyle}
          className="absolute inset-0"
          onPress={() => closeMenu()}
        >
          <Animated.View
            className="bg-chalk "
            style={[
              {
                position: "absolute",
                top: menuPosition.y,
                left: menuPosition.x,
                opacity: opacityAnim,
                transform: [{ scale: scaleAnim }],
                width: menuWidth,
                borderRadius: 14,
                overflow: "hidden",
                borderWidth: 0.5,
                borderColor: themeVars["--color-ash-3"],
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.04,
                shadowRadius: 16,
                elevation: 8,
              },
              isWeb
                ? ({
                    backdropFilter: "blur(4px) saturate(50%) brightness(1)",
                    WebkitBackdropFilter:
                      "blur(4px) saturate(50%) brightness(1)",
                  } as any)
                : {},
            ]}
          >
            <Pressable
              className="py-1 px-1.5"
              onPress={(e) => e.stopPropagation()}
            >
              {items.map((item, index) => (
                <Pressable
                  key={`${item.label}-${index}`}
                  onPress={() => handleItemPress(item, index)}
                  onPressIn={() => setPressedIndex(index)}
                  onPressOut={() => setPressedIndex(null)}
                  className="py-0.5 hover:bg-ash-3/40 hover:rounded-xl"
                >
                  <View
                    className={`rounded-xl px-2.5 py-2 flex-row items-center justify-between ${getBgClass(item, index)}`}
                  >
                    <Text
                      className={`tracking-tight ${getTextClass(item, index)}`}
                    >
                      {item.label}
                    </Text>
                    {item?.rightIcon}
                  </View>
                </Pressable>
              ))}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>
    </>
  );
}
